import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/lib/api";

export default function Login() {
    const { login } = useAuth();
    const navigate = useNavigate();
    const [form, setForm] = useState({ email: "", password: "" });
    const [submitting, setSubmitting] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await login(form.email, form.password);
            toast.success("Welcome back!");
            navigate("/dashboard");
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div data-testid="login-page" className="grid md:grid-cols-2 md:min-h-[calc(100vh-4rem)]">
            <div className="hidden md:block relative overflow-hidden">
                <img
                    src="https://images.unsplash.com/photo-1667322074268-aeeb794ab0d0?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80"
                    alt=""
                    className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-foreground/30" />
                <div className="absolute bottom-10 left-10 right-10 text-background">
                    <h2 className="font-display text-3xl font-bold">Welcome back</h2>
                    <p className="mt-2 text-background/80 max-w-sm">Log in and discover new matches across the world.</p>
                </div>
            </div>
            <div className="flex items-start md:items-center justify-center px-6 pt-6 pb-8 sm:p-12">
                <div className="w-full max-w-md">
                    <h1 className="font-display text-3xl font-bold">Log in</h1>
                    <p className="text-muted-foreground mt-2">No account yet?{" "}
                        <Link to="/register" className="text-primary hover:underline font-medium" data-testid="login-register-link">Sign up</Link>
                    </p>
                    <form onSubmit={submit} className="mt-6 space-y-4">
                        <div>
                            <Label htmlFor="email">Email</Label>
                            <Input
                                id="email" type="email" required
                                data-testid="login-email-input"
                                className="mt-2 rounded-xl h-11"
                                value={form.email}
                                onChange={(e) => setForm({ ...form, email: e.target.value })}
                            />
                        </div>
                        <div>
                            <div className="flex items-baseline justify-between">
                                <Label htmlFor="password">Password</Label>
                                <Link to="/forgot-password" className="text-xs text-primary hover:underline" data-testid="login-forgot-link">
                                    Forgot password?
                                </Link>
                            </div>
                            <Input
                                id="password" type="password" required
                                data-testid="login-password-input"
                                className="mt-2 rounded-xl h-11"
                                value={form.password}
                                onChange={(e) => setForm({ ...form, password: e.target.value })}
                            />
                        </div>
                        <Button
                            type="submit" disabled={submitting}
                            data-testid="login-submit-btn"
                            className="w-full rounded-full h-11 bg-primary hover:bg-primary/90"
                        >
                            {submitting ? "Signing in…" : "Log in"}
                        </Button>
                    </form>
                </div>
            </div>
        </div>
    );
}
